import { ymapsTypes } from '@/entities';
import { TFunction } from 'i18next';

type FindCoordsResult = [number, number] | { error: string } | undefined;

export const findCoordsByAddress = async (
  address: string,
  ymaps: typeof ymapsTypes,
  t: TFunction<'address'>,
): Promise<FindCoordsResult> => {
  if (!ymaps.current || !address.trim()) return;

  try {
    const res = await ymaps.current.geocode(address);

    const firstGeoObject = res.geoObjects.get(0);

    if (firstGeoObject) {
      const coords = firstGeoObject.geometry.getCoordinates();
      return coords;
    }
  } catch {
    return { error: t('map.addressRetrievalError') };
  }
};

type AddressResult =
  | { address: string; success: boolean; addressComponents: { city: string; street: string; house: string } }
  | { error: string };

export const findAddressByCoords = async (
  coords: number[],
  ymaps: typeof ymapsTypes,
  t: TFunction<'address'>,
  areManualCoordinates: boolean,
): Promise<AddressResult> => {
  if (!ymaps.current) return { error: t('map.yandexMapsNotInitialized') };

  try {
    const res = await ymaps.current.geocode(coords, { results: 5 });

    let address: string = t('map.addressNotFound');
    const addressComponents = { city: '', street: '', house: '' };

    res.geoObjects.each((geoObject: typeof ymapsTypes) => {
      const kind = geoObject.properties.get('metaDataProperty.GeocoderMetaData.kind');

      const metaData = geoObject.properties.get('metaDataProperty.GeocoderMetaData');
      if (metaData && metaData.Address && metaData.Address.Components) {
        const components = metaData.Address.Components;

        for (const component of components) {
          switch (component.kind) {
            case 'locality':
              addressComponents.city = component.name;
              break;
            case 'street':
              addressComponents.street = component.name;
              break;
            case 'house':
              addressComponents.house = component.name;
              break;
          }
        }
      }

      if (kind === 'house') {
        address = geoObject.getAddressLine();
        return false;
      } else if (kind === 'street' && address === t('map.addressNotFound')) {
        address = geoObject.getAddressLine();
      } else if (kind === 'district' && areManualCoordinates && address === t('map.addressNotFound')) {
        address = geoObject.getAddressLine().split(', ').slice(1).join(', ');
      }
    });

    return { address, success: address === t('map.addressNotFound') ? false : true, addressComponents };
  } catch {
    return { error: t('map.addressRetrievalError') };
  }
};
